/**
 * Phira's global bezier map (W1-05a engine-eval; rpe-format.md F§6.7, R§2.1, DESIGN.md §6.13, ARCHITECTURE.md §7.1).
 *
 * Phira does not give every bezier event its own curve: at load it scans the chart once and keeps ONE curve per
 * 0.01-quantised key (`Ease.bezierKey`), registered by the FIRST event of that key in this scan order:
 *
 *   lines in export order
 *     -> eventLayers in slot order (null slots skipped): alphaEvents, moveXEvents, moveYEvents, rotateEvents
 *     -> extended: paintEvents, scaleXEvents, scaleYEvents, gifEvents, inclineEvents, textEvents, colorEvents
 *
 * Speed events are never scanned (a speed bezier in the 170 integrator panics unless another event registered the
 * same key). Lists Phira ignores for RENDERING on a line kind but still parses (`IrExtended.ignoredLists`: text /
 * paint on a textured line, gif on a `line.png` line) are scanned at their key's position, from the raw JSON.
 * Every later event with the same key evaluates the first event's exact points.
 *
 * Internal API (consumed by W1-05b engine-graph and the evaluators in this package):
 *   buildBezierRegistry(lines)      the registry of a lowered chart (cheap; rebuild when any line changes)
 *   registry.curve(points)          the prepared solver curve Phira uses for an event with these points
 *   registry.lookup / missing       exact registered points / whether Phira would panic (key never registered)
 *   registry.signature              changes iff the key -> points map changes (recompile bezier users then)
 */

import { createBezierCurve, type BezierCurve } from '../ease/bezier';
import { bezierKey } from '../ease/bezierKey';
import type { BezierPoints } from '../ease/types';
import type { IrExtended, IrLayer, IrLine } from '../ir/types';
import type { Json } from '../util/types';

const fr = Math.fround;

/** Extended list keys in Phira's scan order. */
export const EXTENDED_BEZIER_SCAN = [
  'paintEvents',
  'scaleXEvents',
  'scaleYEvents',
  'gifEvents',
  'inclineEvents',
  'textEvents',
  'colorEvents',
] as const;

/** Layer list keys in Phira's scan order (speed excluded). */
export const LAYER_BEZIER_SCAN = ['alphaEvents', 'moveXEvents', 'moveYEvents', 'rotateEvents'] as const;

export interface BezierRegistry {
  /** Number of registered keys. */
  readonly size: number;
  /** Stable text of the key -> points map; equal signatures mean identical curves for every event. */
  readonly signature: string;
  /** Exact points registered for the key of `p` (the first event of that key in scan order), or null. */
  lookup(p: BezierPoints): BezierPoints | null;
  /** True when no scanned event registered the key of `p` (Phira panics when it evaluates such an event). */
  missing(p: BezierPoints): boolean;
  /**
   * The prepared curve Phira evaluates for an event with points `p`: the registered curve of its key, or, when the
   * key is missing (Phira would panic), the event's own curve so the preview keeps running.
   */
  curve(p: BezierPoints): BezierCurve;
  /** Registered entries in registration order: [key, exact points]. */
  entries(): ReadonlyArray<readonly [key: string, points: BezierPoints]>;
}

/** f32 points as Phira stores them. */
function f32Points(p: BezierPoints): BezierPoints {
  return [fr(p[0]), fr(p[1]), fr(p[2]), fr(p[3])];
}

/** Bezier points of a raw JSON event (`bezier` non-zero), Phira's serde defaults applied; null when not bezier. */
export function rawEventBezier(ev: Json): BezierPoints | null {
  if (typeof ev !== 'object' || ev === null || Array.isArray(ev)) return null;
  const rec = ev as { readonly [k: string]: Json };
  const flag = rec['bezier'];
  if (typeof flag !== 'number' || flag === 0) return null;
  const pts = rec['bezierPoints'];
  if (Array.isArray(pts) && pts.length === 4) {
    const [a, b, c, d] = pts as readonly Json[];
    if (typeof a === 'number' && typeof b === 'number' && typeof c === 'number' && typeof d === 'number') {
      return [a, b, c, d];
    }
  }
  return [0, 0, 0, 0];
}

/** The raw JSON list stored under `key` in `ignoredLists`, or null. */
export function ignoredList(ext: IrExtended, key: string): readonly Json[] | null {
  for (const [name, value] of ext.ignoredLists) {
    if (name === key && Array.isArray(value)) return value as readonly Json[];
  }
  return null;
}

/** The extended object of a line, or null when absent / written as null. */
export function extendedOf(line: IrLine): IrExtended | null {
  const ext = line.extended;
  return ext === 'absent' || ext === null ? null : ext;
}

class RegistryImpl implements BezierRegistry {
  private readonly map = new Map<string, BezierPoints>();
  private readonly curves = new Map<string, BezierCurve>();
  private readonly own = new Map<string, BezierCurve>();
  private sig: string | null = null;

  register(p: BezierPoints): void {
    const key = bezierKey(p);
    if (!this.map.has(key)) {
      this.map.set(key, f32Points(p));
      this.sig = null;
    }
  }

  get size(): number {
    return this.map.size;
  }

  get signature(): string {
    if (this.sig === null) {
      const parts: string[] = [];
      for (const [key, p] of this.map) parts.push(`${key}=${p.join(',')}`);
      this.sig = parts.join(';');
    }
    return this.sig;
  }

  lookup(p: BezierPoints): BezierPoints | null {
    return this.map.get(bezierKey(p)) ?? null;
  }

  missing(p: BezierPoints): boolean {
    return !this.map.has(bezierKey(p));
  }

  curve(p: BezierPoints): BezierCurve {
    const key = bezierKey(p);
    const registered = this.map.get(key);
    if (registered !== undefined) {
      let c = this.curves.get(key);
      if (c === undefined) {
        c = createBezierCurve(registered);
        this.curves.set(key, c);
      }
      return c;
    }
    const ownKey = f32Points(p).join(',');
    let c = this.own.get(ownKey);
    if (c === undefined) {
      c = createBezierCurve(p);
      this.own.set(ownKey, c);
    }
    return c;
  }

  entries(): ReadonlyArray<readonly [string, BezierPoints]> {
    return [...this.map.entries()];
  }
}

function scanTyped(
  reg: RegistryImpl,
  list: ReadonlyArray<{ readonly bezier: number; readonly bezierPoints: BezierPoints }> | undefined,
): void {
  if (list === undefined) return;
  for (const ev of list) if (ev.bezier !== 0) reg.register(ev.bezierPoints);
}

function scanLayer(reg: RegistryImpl, layer: IrLayer): void {
  for (const key of LAYER_BEZIER_SCAN) scanTyped(reg, layer[key]);
}

function scanExtended(reg: RegistryImpl, ext: IrExtended): void {
  for (const key of EXTENDED_BEZIER_SCAN) {
    const typed = ext[key];
    // gifEvents may be `[]` (play automatically): nothing to register.
    scanTyped(reg, typed);
    const raw = ignoredList(ext, key);
    if (raw !== null) {
      for (const ev of raw) {
        const p = rawEventBezier(ev);
        if (p !== null) reg.register(p);
      }
    }
  }
}

/** Phira's bezier map of a lowered chart, in Phira's scan order (first event of each key wins). */
export function buildBezierRegistry(lines: readonly IrLine[]): BezierRegistry {
  const reg = new RegistryImpl();
  for (const line of lines) {
    for (const layer of line.eventLayers) if (layer !== null) scanLayer(reg, layer);
    const ext = extendedOf(line);
    if (ext !== null) scanExtended(reg, ext);
  }
  return reg;
}

/** An empty registry: every bezier event evaluates its own points (used when no chart context exists). */
export function emptyBezierRegistry(): BezierRegistry {
  return new RegistryImpl();
}

/** True when any scanned (or speed) list of the line carries a bezier event. */
export function lineUsesBezier(line: IrLine): boolean {
  const has = (list: ReadonlyArray<{ readonly bezier: number }> | undefined): boolean =>
    list !== undefined && list.some((e) => e.bezier !== 0);
  for (const layer of line.eventLayers) {
    if (layer === null) continue;
    if (has(layer.alphaEvents) || has(layer.moveXEvents) || has(layer.moveYEvents)) return true;
    if (has(layer.rotateEvents) || has(layer.speedEvents)) return true;
  }
  const ext = extendedOf(line);
  if (ext === null) return false;
  return (
    has(ext.scaleXEvents) ||
    has(ext.scaleYEvents) ||
    has(ext.colorEvents) ||
    has(ext.textEvents) ||
    has(ext.inclineEvents) ||
    has(ext.paintEvents) ||
    has(ext.gifEvents)
  );
}
